import type {
  AuthMethod,
  CredentialStatus,
  IamCredentials,
  SsoConfig,
  SsoDeviceAuth,
  SsoAccountInfo,
  SsoRoleInfo,
  AwsCredentials,
  VerifyResult,
} from '@/types'
import { STSClient, GetCallerIdentityCommand } from '@aws-sdk/client-sts'
import type { AwsCredentialIdentity } from '@aws-sdk/types'
import { sanitizeErrorMessage } from './security-utils'
import { API_BASE } from '@/config/api'

// ── Storage Keys ────────────────────────────────────────────────────────
const STORAGE_KEY_IAM = 'bedrock-diag-creds-iam'
const STORAGE_KEY_SSO_TOKEN = 'bedrock-diag-creds-sso-token'
const STORAGE_KEY_SSO_ACCESS_TOKEN = 'bedrock-diag-creds-sso-access-token'
const STORAGE_KEY_SSO_CONFIG = 'bedrock-diag-creds-sso-config'
const STORAGE_KEY_ANTHROPIC = 'bedrock-diag-creds-anthropic-key'
const STORAGE_KEY_XAI = 'bedrock-diag-creds-xai-key'

// ── Constants ───────────────────────────────────────────────────────────
const SSO_EXPIRY_WARNING_MS = 5 * 60 * 1000 // 5 minutes
const INACTIVITY_THRESHOLD_MS = 30 * 60 * 1000 // 30 minutes

// ── Encryption Primitives ───────────────────────────────────────────────
// Module-scoped - never serialized to any storage medium
let cryptoKey: CryptoKey | null = null
let keyCreatedAt: number = 0
export const MAX_SESSION_DURATION_MS = 43_200_000 // 12 hours

const ALL_CREDENTIAL_STORAGE_KEYS = [
  STORAGE_KEY_IAM,
  STORAGE_KEY_SSO_TOKEN,
  STORAGE_KEY_SSO_ACCESS_TOKEN,
  STORAGE_KEY_SSO_CONFIG,
  STORAGE_KEY_ANTHROPIC,
  STORAGE_KEY_XAI,
]

// Callback invoked when key rotation occurs (session duration exceeded)
let onKeyRotationCallback: (() => void) | null = null

export function _setOnKeyRotation(cb: (() => void) | null): void {
  onKeyRotationCallback = cb
}

export async function ensureKey(): Promise<CryptoKey> {
  if (!cryptoKey || (Date.now() - keyCreatedAt > MAX_SESSION_DURATION_MS)) {
    // If key expired, clear all credential entries and notify
    const wasRotation = cryptoKey !== null
    if (wasRotation) {
      ALL_CREDENTIAL_STORAGE_KEYS.forEach(k => sessionStorage.removeItem(k))
    }
    cryptoKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']
    )
    keyCreatedAt = Date.now()
    // Notify CredentialsManager to reset status after rotation
    if (wasRotation && onKeyRotationCallback) {
      onKeyRotationCallback()
    }
  }
  return cryptoKey
}

export async function encryptAndStore(key: string, plaintext: string): Promise<void> {
  const ck = await ensureKey()
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encoded = new TextEncoder().encode(plaintext)
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, ck, encoded)
  const combined = new Uint8Array(iv.length + ciphertext.byteLength)
  combined.set(iv)
  combined.set(new Uint8Array(ciphertext), iv.length)
  sessionStorage.setItem(key, btoa(String.fromCharCode(...combined)))
}

export async function readAndDecrypt(key: string): Promise<string | null> {
  const raw = sessionStorage.getItem(key)
  if (!raw) return null
  try {
    const ck = await ensureKey()
    const bytes = Uint8Array.from(atob(raw), c => c.charCodeAt(0))
    const iv = bytes.slice(0, 12)
    const ciphertext = bytes.slice(12)
    const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, ck, ciphertext)
    return new TextDecoder().decode(decrypted)
  } catch {
    // Corrupted or key mismatch - wipe entry
    sessionStorage.removeItem(key)
    return null
  }
}

// ── Test Helpers (for resetting module-scoped state in tests) ───────────
export function _resetEncryptionState(): void {
  cryptoKey = null
  keyCreatedAt = 0
  onKeyRotationCallback = null
}

export function _setKeyCreatedAt(timestamp: number): void {
  keyCreatedAt = timestamp
}

class CredentialsManager {
  private authMethod: AuthMethod = 'sso'
  private status: CredentialStatus = 'none'
  private error: string | null = null
  private lastActivityTime: number = Date.now()

  constructor() {
    // Register callback so ensureKey() can reset status on key rotation
    _setOnKeyRotation(() => {
      this.status = 'none'
      this.error = null
    })
  }

  // ── Auth Method ─────────────────────────────────────────────────────

  getAuthMethod(): AuthMethod {
    return this.authMethod
  }

  setAuthMethod(method: AuthMethod): void {
    // Clear credentials from the previous auth method to avoid stale state
    ALL_CREDENTIAL_STORAGE_KEYS.forEach(k => sessionStorage.removeItem(k))
    this.authMethod = method
    this.status = 'none'
    this.error = null
  }

  // ── Status ──────────────────────────────────────────────────────────

  getStatus(): CredentialStatus {
    return this.status
  }

  getError(): string | null {
    return this.error
  }

  // ── IAM Credentials ─────────────────────────────────────────────────

  async setIamCredentials(creds: IamCredentials): Promise<void> {
    const accessKeyId = creds.accessKeyId?.trim() ?? ''
    const secretAccessKey = creds.secretAccessKey?.trim() ?? ''

    if (!accessKeyId || !secretAccessKey) {
      this.status = 'none'
      this.error = 'Access Key ID and Secret Access Key are required'
      return
    }

    const toStore: IamCredentials = {
      accessKeyId,
      secretAccessKey,
      ...(creds.sessionToken ? { sessionToken: creds.sessionToken.trim() } : {}),
    }

    await encryptAndStore(STORAGE_KEY_IAM, JSON.stringify(toStore))
    this.status = 'entered'
    this.error = null
    this.touchActivity()
  }

  async getIamCredentials(): Promise<IamCredentials | null> {
    let plaintext = await readAndDecrypt(STORAGE_KEY_IAM)
    if (!plaintext) return null
    try {
      const creds = JSON.parse(plaintext) as IamCredentials
      plaintext = ''  // minimize plaintext window
      return creds
    } catch {
      plaintext = ''
      return null
    }
  }

  async clearCredentials(): Promise<void> {
    ALL_CREDENTIAL_STORAGE_KEYS.forEach(k => sessionStorage.removeItem(k))
    this.status = 'none'
    this.error = null
  }

  // ── Third-Party API Keys ────────────────────────────────────────────

  async setAnthropicApiKey(key: string): Promise<void> {
    const trimmed = key?.trim() ?? ''
    if (!trimmed) {
      sessionStorage.removeItem(STORAGE_KEY_ANTHROPIC)
      return
    }
    await encryptAndStore(STORAGE_KEY_ANTHROPIC, trimmed)
  }

  async getAnthropicApiKey(): Promise<string | null> {
    let plaintext = await readAndDecrypt(STORAGE_KEY_ANTHROPIC)
    if (!plaintext) return null
    const key = plaintext
    plaintext = ''  // minimize plaintext window
    return key
  }

  async clearAnthropicApiKey(): Promise<void> {
    sessionStorage.removeItem(STORAGE_KEY_ANTHROPIC)
  }

  async setXaiApiKey(key: string): Promise<void> {
    const trimmed = key?.trim() ?? ''
    if (!trimmed) {
      sessionStorage.removeItem(STORAGE_KEY_XAI)
      return
    }
    await encryptAndStore(STORAGE_KEY_XAI, trimmed)
  }

  async getXaiApiKey(): Promise<string | null> {
    let plaintext = await readAndDecrypt(STORAGE_KEY_XAI)
    if (!plaintext) return null
    const key = plaintext
    plaintext = ''  // minimize plaintext window
    return key
  }

  async clearXaiApiKey(): Promise<void> {
    sessionStorage.removeItem(STORAGE_KEY_XAI)
  }

  // ── STS Verification (Task 2.2) ─────────────────────────────────────

  async verifyCredentials(): Promise<VerifyResult> {
    const creds = await this.getAwsCredentials()
    if (!creds) {
      const result: VerifyResult = { success: false, error: 'No credentials available' }
      this.status = 'error'
      this.error = result.error!
      console.info(`[CredentialsManager] Verification failed for auth method ${this.authMethod}`)
      return result
    }

    this.status = 'verifying'
    this.error = null

    let stsClient: STSClient | null = null
    try {
      stsClient = new STSClient({
        region: 'us-east-1',
        credentials: {
          accessKeyId: creds.accessKeyId,
          secretAccessKey: creds.secretAccessKey,
          ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
        },
      })

      const response = await stsClient.send(new GetCallerIdentityCommand({}))

      // Zero credential references after SDK use
      stsClient = null

      this.status = 'verified'
      this.error = null
      this.touchActivity()

      const result: VerifyResult = {
        success: true,
        arn: response.Arn ?? undefined,
        account: response.Account ?? undefined,
      }

      console.info(`[CredentialsManager] Verification success for auth method ${this.authMethod}`)
      return result
    } catch (err: unknown) {
      stsClient = null

      const message = err instanceof Error ? err.message : 'Unknown verification error'
      const sanitizedMessage = sanitizeErrorMessage(message)
      const isNetworkError =
        sanitizedMessage.includes('fetch') || sanitizedMessage.includes('network') || sanitizedMessage.includes('ENOTFOUND')
      const errorMsg = isNetworkError
        ? 'Unable to verify credentials. Check your network connection.'
        : sanitizedMessage

      this.status = 'error'
      this.error = errorMsg

      console.info(`[CredentialsManager] Verification failed for auth method ${this.authMethod}`)
      return { success: false, error: errorMsg }
    }
  }

  isVerified(): boolean {
    return this.status === 'verified'
  }

  // ── SSO Device Authorization Flow (Task 2.3) ────────────────────────

  async initiateSsoLogin(config: SsoConfig): Promise<SsoDeviceAuth> {
    await encryptAndStore(STORAGE_KEY_SSO_CONFIG, JSON.stringify(config))

    // Step 1: Register client via backend proxy (avoids CORS on oidc.{region}.amazonaws.com)
    const registerRes = await fetch(`${API_BASE}/sso-oidc/register-client`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region: config.region }),
    })
    if (!registerRes.ok) {
      const err = await registerRes.json().catch(() => ({ error: 'SSO registration failed' }))
      throw new Error(sanitizeErrorMessage(err.error || 'SSO client registration failed'))
    }
    let { clientId, clientSecret } = await registerRes.json()

    // Step 2: Start device authorization via backend proxy
    const deviceRes = await fetch(`${API_BASE}/sso-oidc/start-device-authorization`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        region: config.region,
        clientId,
        clientSecret,
        startUrl: config.startUrl,
      }),
    })
    if (!deviceRes.ok) {
      clientSecret = ''  // memory hygiene
      const err = await deviceRes.json().catch(() => ({ error: 'SSO device auth failed' }))
      throw new Error(sanitizeErrorMessage(err.error || 'SSO device authorization failed'))
    }
    const deviceData = await deviceRes.json()

    const deviceAuth: SsoDeviceAuth = {
      clientId,
      clientSecret,
      deviceCode: deviceData.deviceCode,
      userCode: deviceData.userCode,
      verificationUri: deviceData.verificationUri,
      verificationUriComplete: deviceData.verificationUriComplete,
      expiresAt: new Date(Date.now() + (deviceData.expiresIn ?? 600) * 1000),
      interval: deviceData.interval ?? 5,
    }

    // Memory hygiene - zero local copies after building deviceAuth
    clientSecret = ''

    this.status = 'verifying'
    this.error = null

    return deviceAuth
  }

  async pollSsoToken(deviceAuth: SsoDeviceAuth): Promise<void> {
    let configRaw = await readAndDecrypt(STORAGE_KEY_SSO_CONFIG)
    if (!configRaw) {
      throw new Error('SSO config not found')
    }
    const config: SsoConfig = JSON.parse(configRaw)
    configRaw = ''  // minimize plaintext window

    // Respect server-specified polling interval - never poll faster
    const pollIntervalMs = Math.max(deviceAuth.interval, 5) * 1000

    while (Date.now() < deviceAuth.expiresAt.getTime()) {
      await this.sleep(pollIntervalMs)

      try {
        const tokenRes = await fetch(`${API_BASE}/sso-oidc/create-token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            region: config.region,
            clientId: deviceAuth.clientId,
            clientSecret: deviceAuth.clientSecret,
            deviceCode: deviceAuth.deviceCode,
          }),
        })

        if (tokenRes.status === 428) {
          // Authorization pending - user hasn't completed auth yet
          continue
        }

        if (tokenRes.status === 429) {
          // Slow down - wait extra interval
          await this.sleep(pollIntervalMs)
          continue
        }

        if (tokenRes.status === 410) {
          // Device code expired
          this.status = 'error'
          this.error = 'SSO authorization timed out. Please try again.'
          console.info('[CredentialsManager] SSO token refresh failed')
          throw new Error(this.error)
        }

        if (!tokenRes.ok) {
          const err = await tokenRes.json().catch(() => ({ error: 'SSO token exchange failed' }))
          throw new Error(sanitizeErrorMessage(err.error || 'SSO token exchange failed'))
        }

        const tokenData = await tokenRes.json()

        // Store the SSO access token separately - it's NOT AWS credentials.
        // It must be exchanged via SSO.GetRoleCredentials for real creds.
        await encryptAndStore(STORAGE_KEY_SSO_ACCESS_TOKEN, JSON.stringify({
          accessToken: tokenData.accessToken,
          expiresAt: Date.now() + (tokenData.expiresIn ?? 3600) * 1000,
        }))

        this.status = 'entered'
        this.error = null
        this.touchActivity()

        console.info('[CredentialsManager] SSO access token obtained - need account/role selection')
        return
      } catch (err: unknown) {
        if (err instanceof Error && (err.message.includes('timed out') || err.message.includes('token exchange'))) {
          throw err
        }
        // Unexpected error - treat as fatal
        const message = err instanceof Error ? err.message : 'SSO authorization failed'
        this.status = 'error'
        this.error = sanitizeErrorMessage(message)
        console.info('[CredentialsManager] SSO token refresh failed')
        throw err
      }
    }

    // Timed out
    this.status = 'error'
    this.error = 'SSO authorization timed out. Please try again.'
    console.info('[CredentialsManager] SSO token refresh failed')
    throw new Error(this.error)
  }

  // ── SSO Account & Role Selection ────────────────────────────────────

  async getSsoAccessToken(): Promise<string | null> {
    let raw = await readAndDecrypt(STORAGE_KEY_SSO_ACCESS_TOKEN)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      raw = ''  // minimize plaintext window
      if (parsed.expiresAt && Date.now() >= parsed.expiresAt) {
        sessionStorage.removeItem(STORAGE_KEY_SSO_ACCESS_TOKEN)
        return null
      }
      return parsed.accessToken
    } catch {
      raw = ''
      return null
    }
  }

  async listSsoAccounts(): Promise<SsoAccountInfo[]> {
    let configRaw = await readAndDecrypt(STORAGE_KEY_SSO_CONFIG)
    if (!configRaw) throw new Error('SSO config not found')
    const config: SsoConfig = JSON.parse(configRaw)
    configRaw = ''  // minimize plaintext window
    let accessToken = await this.getSsoAccessToken()
    if (!accessToken) throw new Error('SSO access token not available. Please login again.')

    const res = await fetch(`${API_BASE}/sso/accounts`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region: config.region, accessToken }),
    })
    accessToken = ''  // memory hygiene
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to list accounts' }))
      throw new Error(sanitizeErrorMessage(err.error || 'Failed to list SSO accounts'))
    }
    const data = await res.json()
    return (data.accounts ?? data.accountList ?? []) as SsoAccountInfo[]
  }

  async listSsoRoles(accountId: string): Promise<SsoRoleInfo[]> {
    let configRaw = await readAndDecrypt(STORAGE_KEY_SSO_CONFIG)
    if (!configRaw) throw new Error('SSO config not found')
    const config: SsoConfig = JSON.parse(configRaw)
    configRaw = ''  // minimize plaintext window
    let accessToken = await this.getSsoAccessToken()
    if (!accessToken) throw new Error('SSO access token not available. Please login again.')

    const res = await fetch(`${API_BASE}/sso/roles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region: config.region, accessToken, accountId }),
    })
    accessToken = ''  // memory hygiene
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to list roles' }))
      throw new Error(sanitizeErrorMessage(err.error || 'Failed to list SSO roles'))
    }
    const data = await res.json()
    return (data.roles ?? data.roleList ?? []) as SsoRoleInfo[]
  }

  async assumeSsoRole(accountId: string, roleName: string): Promise<void> {
    let configRaw = await readAndDecrypt(STORAGE_KEY_SSO_CONFIG)
    if (!configRaw) throw new Error('SSO config not found')
    const config: SsoConfig = JSON.parse(configRaw)
    configRaw = ''  // minimize plaintext window
    let accessToken = await this.getSsoAccessToken()
    if (!accessToken) throw new Error('SSO access token not available. Please login again.')

    const res = await fetch(`${API_BASE}/sso/credentials`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ region: config.region, accessToken, accountId, roleName }),
    })
    accessToken = ''  // memory hygiene
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'Failed to get role credentials' }))
      throw new Error(sanitizeErrorMessage(err.error || 'Failed to get SSO role credentials'))
    }
    const data = await res.json()
    const creds = data.roleCredentials || data

    const credentials: AwsCredentials = {
      accessKeyId: creds.accessKeyId,
      secretAccessKey: creds.secretAccessKey,
      sessionToken: creds.sessionToken,
      expiration: new Date(creds.expiration),
    }

    await encryptAndStore(STORAGE_KEY_SSO_TOKEN, JSON.stringify(credentials))
    this.status = 'verified'
    this.error = null
    this.touchActivity()

    console.info('[CredentialsManager] SSO role credentials obtained successfully')
  }

  async getSsoCredentials(): Promise<AwsCredentials | null> {
    let raw = await readAndDecrypt(STORAGE_KEY_SSO_TOKEN)
    if (!raw) return null
    try {
      const parsed = JSON.parse(raw)
      raw = ''  // minimize plaintext window
      return {
        ...parsed,
        expiration: new Date(parsed.expiration),
      } as AwsCredentials
    } catch {
      raw = ''
      return null
    }
  }

  async getSsoExpiration(): Promise<Date | null> {
    const creds = await this.getSsoCredentials()
    return creds?.expiration ?? null
  }

  // ── SSO Expiration & Re-verification (Task 2.4) ─────────────────────

  async needsReVerification(): Promise<boolean> {
    // Check inactivity threshold
    if (Date.now() - this.lastActivityTime > INACTIVITY_THRESHOLD_MS) {
      return true
    }

    // Check SSO expiration proximity
    if (this.authMethod === 'sso') {
      const expiration = await this.getSsoExpiration()
      if (expiration) {
        const timeUntilExpiry = expiration.getTime() - Date.now()
        if (timeUntilExpiry <= SSO_EXPIRY_WARNING_MS) {
          return true
        }
      }
    }

    return false
  }

  /**
   * Check if SSO credentials have fully expired (not just within warning window).
   */
  async isSsoExpired(): Promise<boolean> {
    if (this.authMethod !== 'sso') return false
    const expiration = await this.getSsoExpiration()
    if (!expiration) return false
    return Date.now() >= expiration.getTime()
  }

  /**
   * Check if SSO credentials are within the 5-minute warning window.
   */
  async isSsoExpiringSoon(): Promise<boolean> {
    if (this.authMethod !== 'sso') return false
    const expiration = await this.getSsoExpiration()
    if (!expiration) return false
    const timeUntilExpiry = expiration.getTime() - Date.now()
    return timeUntilExpiry > 0 && timeUntilExpiry <= SSO_EXPIRY_WARNING_MS
  }

  /**
   * Update the SSO credential status based on current expiration state.
   * Call this periodically or before operations to keep status accurate.
   */
  async checkSsoExpiration(): Promise<void> {
    if (this.authMethod !== 'sso') return

    if (await this.isSsoExpired()) {
      this.status = 'expired'
      this.error = 'SSO credentials have expired. Please re-authenticate.'
    }
  }

  /**
   * Check and handle inactivity-based re-verification.
   * Returns true if re-verification is needed.
   */
  checkInactivity(): boolean {
    if (this.status !== 'verified') return false

    if (Date.now() - this.lastActivityTime > INACTIVITY_THRESHOLD_MS) {
      this.status = 'entered'
      this.error = null
      return true
    }
    return false
  }

  /**
   * Record user activity to reset the inactivity timer.
   */
  touchActivity(): void {
    this.lastActivityTime = Date.now()
  }

  // ── API Key Validation ──────────────────────────────────────────────

  async hasRequiredApiKey(profileType: string): Promise<boolean> {
    if (profileType === 'anthropic-direct') {
      return (await this.getAnthropicApiKey()) !== null
    }
    if (profileType === 'grok') {
      return (await this.getXaiApiKey()) !== null
    }
    return true
  }

  // ── Resolved Credentials for SDK ────────────────────────────────────

  async getAwsCredentials(): Promise<AwsCredentialIdentity | null> {
    if (this.authMethod === 'iam') {
      const iam = await this.getIamCredentials()
      if (!iam) return null
      return {
        accessKeyId: iam.accessKeyId,
        secretAccessKey: iam.secretAccessKey,
        ...(iam.sessionToken ? { sessionToken: iam.sessionToken } : {}),
      }
    }

    if (this.authMethod === 'sso') {
      const sso = await this.getSsoCredentials()
      if (!sso) return null
      return {
        accessKeyId: sso.accessKeyId,
        secretAccessKey: sso.secretAccessKey,
        sessionToken: sso.sessionToken,
      }
    }

    return null
  }

  // ── Private Helpers ─────────────────────────────────────────────────

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }
}

export const credentialsManager = new CredentialsManager()
